import api from '@app/services/api';
import type {
    ApiError,
    DashboardDailyStatsViewParams,
    DashboardGroupStatsViewParams,
    DashboardStatsViewResponse,
} from '@app/services/api.types';
import { apiEndpoints } from '@app/utils/constants';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

type QueryOptions = Omit<
    UseQueryOptions<DashboardStatsViewResponse, ApiError>,
    'queryKey' | 'queryFn'
>;

type DailyQueryOptions = Omit<
    UseQueryOptions<DashboardStatsViewResponse, ApiError>,
    'queryKey' | 'queryFn'
>;

export const fetchGroupStats = async (
    params: DashboardGroupStatsViewParams
): Promise<DashboardStatsViewResponse> => {
    const url = apiEndpoints.dashboard.groupStats;
    const response = await api.get(url, { params });
    return response?.data?.data;
};

export const fetchDailyStats = async (
    params: DashboardDailyStatsViewParams
): Promise<DashboardStatsViewResponse> => {
    const url = apiEndpoints.dashboard.dailyStats;
    const response = await api.get(url, { params });
    return response?.data?.data;
};

export const fetchGrouped = async (
    params: DashboardDailyStatsViewParams
): Promise<DashboardStatsViewResponse> => {
    const url = apiEndpoints.dashboard.grouped;
    const response = await api.get(url, { params });
    return response?.data?.data;
};

export const fetchGroupedByBrand = async (
    params: DashboardDailyStatsViewParams
): Promise<DashboardStatsViewResponse> => {
    const url = apiEndpoints.dashboard.byBrand;
    const response = await api.get(url, { params });
    return response?.data?.data;
};

export const fetchGroupedByLocation = async (
    params: DashboardDailyStatsViewParams
): Promise<DashboardStatsViewResponse> => {
    const url = apiEndpoints.dashboard.byLocation;
    const response = await api.get(url, { params });
    return response?.data?.data;
};

const useDashboardStats = () => {
    const useGroupStats = (params: DashboardGroupStatsViewParams, options?: QueryOptions) => {
        return useQuery<DashboardStatsViewResponse, ApiError>({
            queryKey: ['dashboard', 'stats', params],
            queryFn: () => fetchGroupStats(params),
            ...options,
            enabled: Boolean(params.start && params.end),
        });
    };

    const useDailyStats = (params: DashboardDailyStatsViewParams, options?: DailyQueryOptions) => {
        return useQuery<DashboardStatsViewResponse, ApiError>({
            queryKey: ['dashboard', 'daily-stats', params],
            queryFn: () => fetchDailyStats(params),
            ...options,
            enabled: Boolean(params.start && params.end),
        });
    };

    const useGrouped = (params: DashboardDailyStatsViewParams, options?: DailyQueryOptions) => {
        return useQuery<DashboardStatsViewResponse, ApiError>({
            queryKey: ['dashboard', 'grouped', params],
            queryFn: () => fetchGrouped(params),
            ...options,
            enabled: Boolean(params.start && params.end),
        });
    };

    const useGroupedByBrand = (
        params: DashboardDailyStatsViewParams,
        options?: DailyQueryOptions
    ) => {
        return useQuery<DashboardStatsViewResponse, ApiError>({
            queryKey: ['dashboard', 'by-brand', params],
            queryFn: () => fetchGroupedByBrand(params),
            ...options,
            enabled: Boolean(params.start && params.end),
        });
    };

    const useGroupedByLocation = (
        params: DashboardDailyStatsViewParams,
        options?: DailyQueryOptions
    ) => {
        return useQuery<DashboardStatsViewResponse, ApiError>({
            queryKey: ['dashboard', 'by-location', params],
            queryFn: () => fetchGroupedByLocation(params),
            ...options,
            enabled: Boolean(params.start && params.end),
        });
    };

    return {
        useGroupStats,
        useDailyStats,
        useGrouped,
        useGroupedByBrand,
        useGroupedByLocation,
    };
};
export default useDashboardStats;
